import { createSelector, createSlice } from "@reduxjs/toolkit";
import lod_ from "lodash";

/**
 * @typedef {Object} Notification
 * @property {string} id - Identifiant unique de la notification
 * @property {string} severity - Niveau de sévérité : 'info' | 'warning' | 'error' | 'success'
 * @property {Object} message - Contenu de la notification (title + content)
 * @property {string} message.title - Titre de la notification
 * @property {string} message.content - Contenu de la notification
 * @property {string} createdAt - Date de création
 * @property {string} [action] - Type d'action : 'openConversation' | 'openUrl' | 'executeAction'
 * @property {Object} [payload] - Données supplémentaires pour l'action
 * @property {string} [icon] - Nom ou URL de l'icône
 * @property {boolean} read - Indique si la notification a été lue
 * @property {string} [readAt] - Date de lecture
 */

/**
 * @typedef {Object} NotificationsState
 * @property {Notification[]} items - Liste des notifications
 * @property {number} unreadCount - Nombre de notifications non lues
 * @property {boolean} loading - État de chargement
 * @property {string|null} error - Message d'erreur
 */

/** @type {NotificationsState} */
const initialState = {
	items: [],
	unreadCount: 0,
	lastAddedMethod: null
};

const slice = createSlice({
	name: "notifications",
	initialState,
	reducers: {
		/**
		 * Met à jour les notifications après un chargement réussi
		 */
		fetchNotificationsSuccess: (state, action) => {
			const { notifications, unreadCount } = action.payload;
			state.items = notifications;
			state.unreadCount = unreadCount;
			state.lastAddedMethod = "fetch";
		},

		/**
		 * Ajoute une nouvelle notification reçue (WebSocket)
		 */
		notificationReceived: (state, action) => {
			const notification = action.payload;

			if (lod_.isNil(notification) || lod_.isNil(notification.id)) {
				return;
			}

			// Éviter les doublons
			const exists = state.items.some(item => item.id === notification.id);
			if (exists === true) {
				return;
			}

			state.items.unshift({
				...notification,
				read: notification.read ?? false
			});

			if (notification.read !== true) {
				state.unreadCount += 1;
			}
			state.lastAddedMethod = "websocket";
		},

		/**
		 * Met à jour une notification
		 */
		updateNotification: (state, action) => {
			const updatedNotification = action.payload;

			if (lod_.isNil(updatedNotification) || lod_.isNil(updatedNotification.id)) {
				return;
			}

			const index = state.items.findIndex(item => item.id === updatedNotification.id);

			if (index === -1) {
				return;
			}

			state.items[index] = {
				...state.items[index],
				...updatedNotification
			};
		},

		/**
		 * Marque une notification comme lue
		 */
		markNotificationAsRead: (state, action) => {
			const { id, readAt } = action.payload;

			if (lod_.isNil(id)) {
				return;
			}

			const notification = state.items.find(item => item.id === id);

			if (lod_.isNil(notification)) {
				return;
			}

			if (notification.read !== true) {
				notification.read = true;
				notification.readAt = readAt;
				state.unreadCount = Math.max(0, state.unreadCount - 1);
			}
		},

		/**
		 * Marque toutes les notifications comme lues
		 */
		markAllNotificationsAsRead: (state, action) => {
			const readAt = action.payload?.readAt || new Date().toISOString();

			state.items.forEach(notification => {
				if (notification.read !== true) {
					notification.read = true;
					notification.readAt = readAt;
				}
			});

			state.unreadCount = 0;
		},

		/**
		 * Supprime une notification
		 */
		removeNotification: (state, action) => {
			const { id } = action.payload;

			if (lod_.isNil(id)) {
				return;
			}

			const notificationIndex = state.items.findIndex(item => item.id === id);

			if (notificationIndex === -1) {
				return;
			}

			const notification = state.items[notificationIndex];

			if (notification.read !== true) {
				state.unreadCount = Math.max(0, state.unreadCount - 1);
			}

			state.items.splice(notificationIndex, 1);
		},

		/**
		 * Met à jour le compteur de notifications non lues
		 */
		setUnreadCount: (state, action) => {
			const count = action.payload;

			if (typeof count === "number" && count >= 0) {
				state.unreadCount = count;
			}
		},

		/**
		 * Réinitialise les notifications
		 */
		clearNotifications: state => {
			state.items = [];
			state.unreadCount = 0;
			state.error = null;
		},

		/**
		 * Efface l'erreur
		 */
		clearError: state => {
			state.error = null;
		}
	}
});

// Sélecteurs
export const selectNotifications = state => state.notifications;
export const selectNotificationsItems = state => state.notifications.items;
export const selectUnreadCount = state => state.notifications.unreadCount;
export const selectNotificationsLoading = state => state.notifications.loading;
export const selectNotificationsError = state => state.notifications.error;

export const {
	fetchNotificationsRequest,
	fetchNotificationsSuccess,
	fetchNotificationsError,
	notificationReceived,
	updateNotification,
	markNotificationAsRead,
	markAllNotificationsAsRead,
	removeNotification,
	setUnreadCount,
	clearNotifications,
	clearError
} = slice.actions;

export default slice.reducer;
